import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Key, CreditCard, Settings, HelpCircle, Trash2, ArrowRight } from 'lucide-react'
import { BalanceHeroCard } from '@/components/account/BalanceHeroCard'
import { useLang } from '@/hooks/useLang'
import { AccountLayout } from './-layout'

export const Route = createFileRoute('/$lang/account/')({
  component: OverviewRoute,
})

function OverviewRoute() {
  const { t } = useTranslation(['app', 'common'])
  const lang = useLang()
  const navigate = useNavigate()

  const handleTopUp = () => {
    navigate({ to: '/$lang/account/billing', search: { topup: 'true' } })
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        {/* Balance Hero */}
        <BalanceHeroCard
          onTopUp={handleTopUp}
          compact
          secondaryCta={
            <Button
              variant="ghost"
              shape="pill"
              className="border border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
              asChild
            >
              <Link to="/$lang/account/billing" params={{ lang }} search={{}}>
                <CreditCard className="mr-2 h-4 w-4" />
                {t("account.tabs.billing")}
              </Link>
            </Button>
          }
        />

        {/* Quick action cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/$lang/account/billing" params={{ lang }} search={{}} className="block">
            <Card className="cursor-pointer transition-colors hover:bg-muted/50 h-full">
              <CardContent className="flex items-start gap-4 p-6">
                <div className="rounded-lg bg-primary/10 p-3">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">{t("account.transactionHistory")}</h3>
                  <p className="text-sm text-muted-foreground">{t("account.recentTopUps")}</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/$lang/account/api" params={{ lang }} className="block">
            <Card className="cursor-pointer transition-colors hover:bg-muted/50 h-full">
              <CardContent className="flex items-start gap-4 p-6">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Key className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">{t("apiKeys.title")}</h3>
                  <p className="text-sm text-muted-foreground">{t("apiKeys.description")}</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/$lang/account/settings" params={{ lang }} className="block">
            <Card className="cursor-pointer transition-colors hover:bg-muted/50 h-full">
              <CardContent className="flex items-start gap-4 p-6">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Settings className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-foreground">{t("settings.title")}</h3>
                  <p className="text-sm text-muted-foreground">{t("settings.description")}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Help section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          <Card className="border border-border/40 bg-muted/30 flex flex-col">
            <CardContent className="p-5 flex flex-col flex-1">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <HelpCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{t("account.needHelpPrompt")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      <Link
                        to="/$lang/faq"
                        params={{ lang }}
                        className="text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("account.faqLink")}
                      </Link>
                      {" "}{t("account.orContactSupport")}
                    </p>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    asChild
                    className="h-auto p-0 gap-1 justify-start"
                  >
                    <a href="mailto:support@phonesalad.com">
                      {t("errors.contactSupport")}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/40 bg-muted/30 flex flex-col">
            <CardContent className="p-5 flex flex-col flex-1">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{t("account.deleteAccount.title")}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("account.deleteAccount.description", { email: "support@phonesalad.com" })}
                    </p>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    asChild
                    className="h-auto p-0 gap-1 justify-start"
                  >
                    <a href="mailto:support@phonesalad.com?subject=Account Deletion Request">
                      {t("errors.contactSupport")}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AccountLayout>
  )
}
